import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams, useLocation } from "react-router-dom";
import {
    AppDispatch,
    googleLoginAction,
    toggleLoading,
    setJwt,
    initializeAccountDetail,
    fetchRedeemer,
    fetchBookNowRedeemer,
    fetchBookNowItems,
    setBookNowItemData,
    store,
} from "@redux";
import { fetchCartItems } from "../checkout";
import { cartApi } from "@api";
import { sessionHelper } from "@utilities";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { IGoogleLogin } from "@interfaces";
import { PATH_BOOK_NOW } from "@routes";
import { hasPlaceholderProfile } from "../signupOfferConfig";
import { ensurePointsAgentUpgrade, looksLikeNewSocialAccount } from "../pointsAgentUpgrade";
import { resolveUpgradeNames } from "../socialSignupNames";
import { fetchGoogleProfileNames } from "src/Utilities/googleProfile";

interface UseGoogleOAuthProps {
    isProcessingOAuth: boolean;
    setIsProcessingOAuth: (processing: boolean) => void;
    sessionId?: string;
}

export const useGoogleOAuth = ({
    isProcessingOAuth,
    setIsProcessingOAuth,
    sessionId,
}: UseGoogleOAuthProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    useEffect(() => {
        const hash = window.location.hash;
        if (hash.includes("access_token") && !isProcessingOAuth) {
            const params = new URLSearchParams(hash.substring(1));
            const accessToken = params.get("access_token");
            if (accessToken) {
                setIsProcessingOAuth(true);
                dispatch(toggleLoading(true));
                const values: IGoogleLogin = { google_access_token: accessToken };
                dispatch(googleLoginAction(values))
                    .then(async (res: any) => {
                        if (res.payload?.data?.accessToken) {
                            const { lsJwt } = await import("src/Utilities/storage");
                            lsJwt.setItem(res.payload.data);

                            const isBookNow = location.pathname.includes(PATH_BOOK_NOW);
                            const urlSessionId = searchParams.get("sessionId");
                            let currentSessionId = urlSessionId || undefined;

                            if (isBookNow) {
                                if (!currentSessionId && sessionHelper.hasBookNowSession()) {
                                    currentSessionId = sessionHelper.getBookNowSessionId();
                                }
                            } else {
                                if (!currentSessionId && sessionHelper.hasSession()) {
                                    currentSessionId = sessionHelper.getSessionId();
                                }
                            }

                            // Use the sessionId prop if available
                            if (!currentSessionId && sessionId) {
                                currentSessionId = sessionId;
                            }
                            // Ensure sessionId is in URL so it persists after refresh
                            if (currentSessionId && !urlSessionId) {
                                setSearchParams({ sessionId: currentSessionId });
                            }

                            if (currentSessionId) {
                                try {
                                    await cartApi.mergeSessionCart(
                                        currentSessionId,
                                        res.payload.data.accessToken,
                                    );
                                    await dispatch(setJwt(res.payload.data));

                                    if (isBookNow) {
                                        // Fetch BookNow data
                                        const fetchedItems = await dispatch(
                                            fetchBookNowItems(currentSessionId),
                                        ).unwrap();
                                        dispatch(setBookNowItemData(fetchedItems));
                                        const redeemers = await dispatch(
                                            fetchBookNowRedeemer(currentSessionId),
                                        ).unwrap();

                                        // Initialize account and get fresh account data
                                        await dispatch(initializeAccountDetail());
                                        const { selectAccount } = await import("@redux");
                                        const accountState = selectAccount(store.getState());
                                        const freshAccount = accountState?.account;

                                        // Auto-add user as redeemer if profile is complete
                                        // A profile still holding the sign-up
                                        // stand-ins passes every field test below,
                                        // so it would seed a traveller with a 1900
                                        // date of birth and a zeroed phone number.
                                        const hasCompleteProfile =
                                            freshAccount?.title &&
                                            freshAccount?.first_name &&
                                            freshAccount?.last_name &&
                                            freshAccount?.email &&
                                            freshAccount?.phone_number &&
                                            freshAccount?.post_code &&
                                            freshAccount?.country_code &&
                                            !hasPlaceholderProfile(freshAccount);

                                        const userAlreadyExists = redeemers.some(
                                            (r) =>
                                                r.email?.toLowerCase() ===
                                                freshAccount?.email?.toLowerCase(),
                                        );

                                        if (hasCompleteProfile && !userAlreadyExists) {
                                            try {
                                                const { redeemerApi } =
                                                    await import("src/Api/redeemers");
                                                const userTravellerData = {
                                                    title: freshAccount.title!,
                                                    firstName: freshAccount.first_name!,
                                                    lastName: freshAccount.last_name!,
                                                    email: freshAccount.email!,
                                                    phoneNumber: freshAccount.phone_number!,
                                                    postalCode: freshAccount.post_code!,
                                                    countryCode: freshAccount.country_code!,
                                                    dateOfBirth: freshAccount.date_of_birth || "",
                                                    customerIndex: 0,
                                                };

                                                await redeemerApi.AddRedeemerBookNow(
                                                    userTravellerData,
                                                    currentSessionId,
                                                );
                                                await dispatch(
                                                    fetchBookNowRedeemer(currentSessionId),
                                                ).unwrap();
                                            } catch (error) {
                                                console.error(
                                                    "Failed to add user as BookNow redeemer:",
                                                    error,
                                                );
                                            }
                                        }

                                        if (sessionHelper.hasBookNowSession()) {
                                            sessionHelper.clearBookNowSession();
                                        }
                                    } else {
                                        // Fetch cart data
                                        await dispatch(fetchCartItems(currentSessionId));
                                        const redeemers = await dispatch(
                                            fetchRedeemer(currentSessionId),
                                        ).unwrap();

                                        // Initialize account and get fresh account data
                                        await dispatch(initializeAccountDetail());
                                        const { selectAccount } = await import("@redux");
                                        const accountState = selectAccount(store.getState());
                                        const freshAccount = accountState?.account;

                                        // Auto-add user as redeemer if profile is complete
                                        // A profile still holding the sign-up
                                        // stand-ins passes every field test below,
                                        // so it would seed a traveller with a 1900
                                        // date of birth and a zeroed phone number.
                                        const hasCompleteProfile =
                                            freshAccount?.title &&
                                            freshAccount?.first_name &&
                                            freshAccount?.last_name &&
                                            freshAccount?.email &&
                                            freshAccount?.phone_number &&
                                            freshAccount?.post_code &&
                                            freshAccount?.country_code &&
                                            !hasPlaceholderProfile(freshAccount);

                                        const userAlreadyExists = redeemers.some(
                                            (r) =>
                                                r.email?.toLowerCase() ===
                                                freshAccount?.email?.toLowerCase(),
                                        );

                                        if (hasCompleteProfile && !userAlreadyExists) {
                                            try {
                                                const { redeemerApi } =
                                                    await import("src/Api/redeemers");
                                                const userTravellerData = {
                                                    title: freshAccount.title!,
                                                    firstName: freshAccount.first_name!,
                                                    lastName: freshAccount.last_name!,
                                                    email: freshAccount.email!,
                                                    phoneNumber: freshAccount.phone_number!,
                                                    postalCode: freshAccount.post_code!,
                                                    countryCode: freshAccount.country_code!,
                                                    dateOfBirth: freshAccount.date_of_birth || "",
                                                    customerIndex: 0,
                                                };

                                                await redeemerApi.AddRedeemer(
                                                    userTravellerData,
                                                    currentSessionId,
                                                );
                                                await dispatch(
                                                    fetchRedeemer(currentSessionId),
                                                ).unwrap();
                                            } catch (error) {
                                                console.error(
                                                    "Failed to add user as redeemer:",
                                                    error,
                                                );
                                            }
                                        }

                                        if (sessionHelper.hasSession()) {
                                            sessionHelper.clearSession();
                                        }
                                    }
                                } catch (error) {
                                    console.error("Failed to merge session:", error);
                                }
                            } else {
                                await dispatch(setJwt(res.payload.data));
                                await dispatch(initializeAccountDetail());
                            }
                        }
                        // A Google sign-in cannot say whether it just created the
                        // account, so an account that has never been through the
                        // traveller form is taken to be a new one and upgraded.
                        // Failures stay silent here: this is an ordinary sign-in as
                        // far as the visitor is concerned.
                        const { selectAccount } = await import("@redux");
                        const signedInAccount = selectAccount(store.getState())?.account;
                        if (looksLikeNewSocialAccount(signedInAccount)) {
                            // The upgrade mints the account's permanent branch
                            // identity from the name it finds, so a real one is
                            // fetched from Google first and the upgrade skipped
                            // rather than run under a stand-in.
                            const names = await resolveUpgradeNames(signedInAccount, () =>
                                fetchGoogleProfileNames(accessToken),
                            );
                            if (names) {
                                await ensurePointsAgentUpgrade({
                                    email: signedInAccount?.email ?? "",
                                    account: signedInAccount,
                                    signupNames: names,
                                    dispatch,
                                });
                            }
                        }

                        window.history.replaceState(
                            {},
                            document.title,
                            window.location.pathname + window.location.search,
                        );
                    })
                    .catch((error: any) => {
                        console.error("Login error:", error);
                        ErrorToast({ Message: `${error}` });
                    })
                    .finally(() => {
                        setIsProcessingOAuth(false);
                        dispatch(toggleLoading(false));
                    });
            }
        }
    }, [
        dispatch,
        isProcessingOAuth,
        searchParams,
        sessionId,
        setIsProcessingOAuth,
        setSearchParams,
        location,
    ]);
};
